(() => {
  if (customElements.get('avontic-watch-conversion')) return;

  class AvonticWatchConversion extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.status = this.querySelector('[data-watch-status]');
      this.variantMedia = this.readVariantMedia();
      this.boundClick = this.handleClick.bind(this);
      this.boundToggle = this.handleToggle.bind(this);
      this.boundVariant = this.handleVariant.bind(this);
      this.boundChange = this.handleNativeChange.bind(this);
      this.addEventListener('click', this.boundClick);
      this.addEventListener('toggle', this.boundToggle, true);
      document.addEventListener('variant:change', this.boundVariant);
      document.addEventListener('change', this.boundChange, true);
      this.observeImpressions();
      this.syncFromNativeVariant();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundClick);
      this.removeEventListener('toggle', this.boundToggle, true);
      document.removeEventListener('variant:change', this.boundVariant);
      document.removeEventListener('change', this.boundChange, true);
      this.observer?.disconnect();
      delete this.dataset.ready;
    }

    readVariantMedia() {
      try {
        const data = JSON.parse(this.querySelector('[data-watch-variant-media]')?.textContent || '[]');
        return new Map(data.map((item) => [String(item.id), String(item.mediaId || '')]));
      } catch (_) {
        return new Map();
      }
    }

    handleClick(event) {
      const option = event.target.closest('[data-watch-select-option]');
      if (option) {
        this.selectExactNativeOption(option);
        return;
      }

      const configure = event.target.closest('[data-watch-return-configuration]');
      if (configure) {
        this.returnToConfiguration();
        this.track('av_watch_return_configuration', { action: 'return_to_configuration' });
        return;
      }

      const comparison = event.target.closest('[data-watch-comparison-link]');
      if (comparison) {
        this.track('av_watch_comparison_click', {
          action: 'choose_options',
          recommended_product_id: comparison.dataset.productId
        });
        return;
      }

      if (event.target.closest('[data-watch-review-link]')) {
        this.track('av_watch_review_click', { action: 'read_reviews' });
      }
    }

    handleToggle(event) {
      const details = event.target.closest('[data-watch-faq]');
      if (!details?.open) return;
      const position = Array.from(this.querySelectorAll('[data-watch-faq]')).indexOf(details) + 1;
      this.track('av_watch_faq_open', { position, action: 'open' });
    }

    handleVariant(event) {
      const variant = event.detail?.variant;
      if (!variant) return;
      const mediaId = variant.featured_media?.id || variant.featured_media_id || this.variantMedia.get(String(variant.id));
      this.activateMedia(mediaId);
    }

    handleNativeChange(event) {
      if (!event.target.closest('#avontic-primary-product variant-picker')) return;
      window.setTimeout(() => this.syncFromNativeVariant(), 0);
    }

    selectExactNativeOption(button) {
      const exactValue = button.dataset.optionValue || '';
      const picker = document.querySelector('#avontic-primary-product variant-picker');
      if (!picker || !exactValue) {
        this.setStatus(this.dataset.optionNotFoundMessage);
        this.returnToConfiguration();
        return;
      }

      const matches = Array.from(picker.querySelectorAll('label[for]')).filter((label) => {
        const input = document.getElementById(label.htmlFor);
        return input && !input.disabled && label.textContent.trim() === exactValue;
      });

      if (matches.length !== 1) {
        this.setStatus(matches.length > 1 ? this.dataset.optionAmbiguousMessage : this.dataset.optionNotFoundMessage);
        this.track('av_watch_exact_option_select', {
          action: 'exact_option_assist',
          result: matches.length > 1 ? 'ambiguous' : 'not_found'
        });
        this.returnToConfiguration();
        return;
      }

      const input = document.getElementById(matches[0].htmlFor);
      input.click();
      input.focus({ preventScroll: true });
      this.setStatus(this.dataset.optionSelectedMessage);
      this.track('av_watch_exact_option_select', {
        action: 'exact_option_assist',
        result: 'selected'
      });
      this.returnToConfiguration(false);
    }

    returnToConfiguration(moveFocus = true) {
      const picker = document.querySelector('#avontic-primary-product variant-picker');
      const target = picker || document.querySelector('#avontic-primary-product');
      if (!target) return;
      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center'
      });
      if (moveFocus) {
        const focusTarget = target.querySelector('input:not([disabled]), button:not([disabled]), select:not([disabled])');
        window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), 250);
      }
    }

    syncFromNativeVariant() {
      try {
        const data = JSON.parse(document.querySelector('#avontic-primary-product variant-picker script[data-variant]')?.textContent || 'null');
        if (!data) return;
        const mediaId = data.featured_media?.id || data.featured_media_id || this.variantMedia.get(String(data.id));
        this.activateMedia(mediaId);
      } catch (_) {
        return;
      }
    }

    activateMedia(mediaId) {
      if (!mediaId) return;
      const items = Array.from(this.querySelectorAll('[data-watch-media-id]'));
      const active = items.find((item) => item.dataset.watchMediaId === String(mediaId));
      if (!active) return;

      if (active.classList.contains('is-active')) {
        items.forEach((item) => {
          if (item === active) item.setAttribute('aria-current', 'true');
          else item.removeAttribute('aria-current');
        });
        return;
      }

      const apply = () => {
        items.forEach((item) => {
          const current = item === active;
          item.classList.toggle('is-active', current);
          if (current) item.setAttribute('aria-current', 'true');
          else item.removeAttribute('aria-current');
        });
      };

      if (document.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const transition = document.startViewTransition(apply);
        transition.ready?.catch(() => {});
        transition.finished?.catch(() => {});
      } else {
        apply();
      }

      active.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
      this.setStatus(this.dataset.mediaActiveMessage);
    }

    observeImpressions() {
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.35) return;
          const eventName = entry.target.matches('[data-watch-size-guide]')
            ? 'av_watch_size_guide_view'
            : entry.target.matches('[data-watch-comparison]')
              ? 'av_watch_comparison_view'
              : 'av_watch_finish_view';
          this.track(eventName, { visibility_ratio: Math.round(entry.intersectionRatio * 100) / 100 }, {
            oncePerSession: true,
            dedupeKey: `${this.dataset.sectionId}:${eventName}`
          });
          this.observer.unobserve(entry.target);
        });
      }, { threshold: [0.35] });

      this.querySelectorAll('[data-watch-size-guide], [data-watch-finish], [data-watch-comparison]').forEach((item) => this.observer.observe(item));
    }

    track(name, payload = {}, options = {}) {
      window.AvonticAnalytics?.track?.(name, {
        product_id: this.dataset.productId,
        product_handle: this.dataset.productHandle,
        component: 'watch_conversion',
        component_instance: this.dataset.sectionId,
        ...payload
      }, options);
    }

    setStatus(message) {
      if (!this.status || !message) return;
      this.status.textContent = '';
      window.requestAnimationFrame(() => {
        this.status.textContent = message;
      });
    }
  }

  customElements.define('avontic-watch-conversion', AvonticWatchConversion);
})();
